import { Helmet } from "react-helmet-async";

const SITE_URL = "https://goldensea-conciergerie.fr";
const SITE_NAME = "Golden Sea Conciergerie";
const DEFAULT_IMAGE = `${SITE_URL}/images/og-image.jpg`;

export default function SEO({
  title,
  description,
  path = "",
  image,
  type = "website",
  article,
  noindex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Conciergerie premium bord de mer`;
  const fullUrl = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Article specific */}
      {article && <meta property="article:published_time" content={article.published_at} />}
      {article && <meta property="article:author" content={SITE_NAME} />}

      {/* Noindex */}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Geo */}
      <meta name="geo.region" content="FR-30" />
      <meta name="geo.placename" content="Aigues-Mortes" />
    </Helmet>
  );
}

// JSON-LD components
export function LocalBusinessSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#organization`,
    name: "Golden Sea Conciergerie",
    description: "Conciergerie premium pour la gestion de locations saisonnieres a La Grande-Motte, Aigues-Mortes, Le Grau-du-Roi et Carnon. Gestion complete, sous-location, accueil voyageurs.",
    url: SITE_URL,
    telephone: "+33615166415",
    email: "contact@goldensea-conciergerie.fr",
    address: {
      "@type": "PostalAddress",
      streetAddress: "2 Impasse du Flamant",
      addressLocality: "Aigues-Mortes",
      postalCode: "30220",
      addressCountry: "FR",
      addressRegion: "Occitanie",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 43.567,
      longitude: 4.189,
    },
    areaServed: [
      { "@type": "City", name: "La Grande-Motte" },
      { "@type": "City", name: "Aigues-Mortes" },
      { "@type": "City", name: "Le Grau-du-Roi" },
      { "@type": "City", name: "Carnon" },
    ],
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "08:00",
      closes: "20:00",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: "14",
      bestRating: "5",
    },
    priceRange: "$$",
    image: `${SITE_URL}/images/logo.png`,
    sameAs: [
      "https://www.facebook.com/people/Golden-SEA-Conciergerie-Val%C3%A9rie-Mayaud/61578043644997/",
      "https://www.instagram.com/golden_sea_conciergerie",
      "https://fr.linkedin.com/in/val%C3%A9rie-mayaud-5a8548187",
    ],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function ArticleSchema({ post }) {
  if (!post) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image_url,
    datePublished: post.published_at,
    author: {
      "@type": "Organization",
      name: "Golden Sea Conciergerie",
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "Golden Sea Conciergerie",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/images/logo.png`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/nos-actualites/${post.slug}`,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function FAQSchema({ faqs }) {
  if (!faqs || faqs.length === 0) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function BreadcrumbSchema({ items }) {
  if (!items || items.length === 0) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function ServiceSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    provider: {
      "@type": "LocalBusiness",
      name: "Golden Sea Conciergerie",
    },
    serviceType: "Conciergerie locative saisonniere",
    areaServed: [
      { "@type": "City", name: "La Grande-Motte" },
      { "@type": "City", name: "Aigues-Mortes" },
      { "@type": "City", name: "Le Grau-du-Roi" },
      { "@type": "City", name: "Carnon" },
    ],
    description: "Gestion complete de votre location saisonniere : creation d'annonces, tarification dynamique, accueil voyageurs, menage professionnel, communication 7j/7.",
    offers: {
      "@type": "Offer",
      description: "Commission de 23% du CA genere - Devis gratuit et sans engagement",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
